import { useEffect, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { Button } from "@themes/pollinator/ui/Button";
import { createSlug } from "@core/utils/createSlug";

export type Props = {
  /** MDX content string to extract headings from */
  content: string;
  /** Additional CSS classes */
  className?: string;
  /** Additional CSS classes for inner container */
  innerClassName?: string;
  /** Scroll container element for scroll spy functionality */
  scrollContainer?: HTMLElement | null;
};

/**
 * Table of contents component for MDX content with scroll spy.
 */
export function MdxRendererTableOfContents({
  content,
  className,
  innerClassName,
  scrollContainer,
}: Props) {
  const [activeHash, setActiveHash] = useState<string | null>(null);
  const isUserClickingRef = useRef(false);
  const clickedButtonRef = useRef<HTMLElement | null>(null);

  const tocContainerRef = useRef<HTMLDivElement>(null);

  const headings = useMemo(() => {
    const regex = /^# (.*?)$/gm;
    const headings: string[] = [];
    let match;

    while ((match = regex.exec(content)) !== null) {
      headings.push(match[1].trim());
    }

    return headings;
  }, [content]);

  useEffect(() => {
    if (activeHash) return;
    if (!headings) return;
    if (headings.length === 0) return;

    setActiveHash(`#${createSlug(headings[0])}`);
  }, [activeHash, headings]);

  // Scroll spy functionality
  useEffect(() => {
    if (!scrollContainer || !headings.length) return;

    const handleScroll = () => {
      const scrollTop = scrollContainer.scrollTop;
      const scrollHeight = scrollContainer.scrollHeight;
      const clientHeight = scrollContainer.clientHeight;

      // Get all heading elements
      const headingElements = headings
        .map((heading) => document.getElementById(createSlug(heading)))
        .filter(Boolean) as HTMLElement[];

      if (headingElements.length === 0) return;

      // If we're at the bottom of the scroll area, activate the last heading
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        const lastHeading = headings[headings.length - 1];
        setActiveHash(`#${createSlug(lastHeading)}`);
        return;
      }

      // Find the heading that's currently visible
      let currentHeading = headings[0];

      for (let i = 0; i < headingElements.length; i++) {
        const element = headingElements[i];
        const rect = element.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();

        // Check if the heading is visible in the scroll container
        const elementTop = rect.top - containerRect.top;

        if (elementTop <= 100) {
          // 100px threshold from the top
          currentHeading = headings[i];
        } else {
          break;
        }
      }

      const newActiveHash = `#${createSlug(currentHeading)}`;
      if (newActiveHash !== activeHash) {
        setActiveHash(newActiveHash);

        // Center the new active TOC element and reset focus if not user-initiated
        const tocContainer = tocContainerRef.current;
        if (tocContainer) {
          const activeButton = tocContainer.querySelector(
            `a[href="${newActiveHash}"]`
          ) as HTMLElement;

          // Only remove focus if this wasn't triggered by a user click
          if (!isUserClickingRef.current) {
            const focusedElement = document.activeElement as HTMLElement;
            if (focusedElement && tocContainer.contains(focusedElement)) {
              focusedElement.blur();
            }
          } else if (clickedButtonRef.current) {
            // If this was a user click, ensure the clicked button stays focused
            clickedButtonRef.current.focus();
          }

          if (activeButton) {
            tocContainer.scrollTo({
              left:
                activeButton.offsetLeft -
                tocContainer.offsetWidth / 2 +
                activeButton.offsetWidth / 2,
              top: 0,
              behavior: "smooth",
            });
          }
        }
      }
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });

    // Initial check
    handleScroll();

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, [scrollContainer, headings, activeHash]);

  const scrollToElementByHash = (
    hash: string,
    e: React.MouseEvent<HTMLAnchorElement>
  ) => {
    const element = document.getElementById(hash.slice(1));
    const tocElement = e.currentTarget;
    const tocContainer = tocContainerRef.current;

    // Execute both scroll operations simultaneously
    // 1. Scroll main content to the target element
    if (element && scrollContainer) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const scrollTop = scrollContainer.scrollTop;

      // Calculate the position to scroll to (with some offset from the top)
      const targetScrollTop =
        scrollTop + (elementRect.top - containerRect.top) - 96;

      scrollContainer.scrollTo({
        top: targetScrollTop,
        behavior: "smooth",
      });
    } else if (element) {
      // Fallback to default behavior if no scroll container
      element.scrollIntoView({ behavior: "smooth" });
    }

    // 2. Center the TOC button (happens in parallel with the above)
    if (tocContainer) {
      tocContainer.scrollTo({
        left:
          tocElement.offsetLeft -
          tocContainer.offsetWidth / 2 +
          tocElement.offsetWidth / 2,
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const handleClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    hash: string
  ) => {
    e.preventDefault();

    // Mark that this is a user-initiated action BEFORE any scrolling happens
    isUserClickingRef.current = true;
    clickedButtonRef.current = e.currentTarget as HTMLElement;

    setActiveHash(hash);

    if (hash) {
      scrollToElementByHash(hash, e);
    }

    // Reset the flag after a longer delay to ensure all scroll events have settled
    setTimeout(() => {
      isUserClickingRef.current = false;
      clickedButtonRef.current = null;
    }, 1000);
  };

  if (headings.length === 0) return null;

  return (
    <div
      className={twMerge(
        "w-full bg-brand-50 pt-4 pb-8 overflow-x-auto flex flex-row items-center gap-2",
        className
      )}
      ref={tocContainerRef}
      data-testid="mdx-renderer-toc"
    >
      <div
        className={twMerge(
          "flex flex-wrap gap-2 sticky top-0 z-10",
          innerClassName
        )}
      >
        {headings.map((heading) => (
          <Button
            as="a"
            key={heading}
            variant={
              activeHash === `#${createSlug(heading)}` ? "secondary" : "link"
            }
            href={`#${createSlug(heading)}`}
            className={twMerge(
              "whitespace-nowrap",
              activeHash === `#${createSlug(heading)}` && "bg-brand-100"
            )}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
              handleClick(e, `#${createSlug(heading)}`)
            }
            data-testid={`mdx-renderer-toc-item-${createSlug(heading)}`}
          >
            {heading}
          </Button>
        ))}
      </div>
    </div>
  );
}

export default MdxRendererTableOfContents;
